// Drift: Automatiseringer og sendte beskeder på Supabase (del af SupabaseApi – se supabase.ts).
// Reglerne ligger i tabellen workflows (ingen rækker = standarden; første gang en regel gemmes, lægges standarden ind, så
// ingen forsvinder). Serveren sender dem (edge-funktionen "automation" med src/core/automationMotor.ts); her vises planlagte
// job og loggen fra automation_koersler, og "Vis resultat" fletter teksten med de samme funktioner som serveren.
import type { Api } from './api';
import type { Besked } from '../../core/typer';
import { STANDARD_WORKFLOWS, koertid, opfyldt, type Workflow } from '../../core/automation';
import { autoKlient, byggBesked, kontekstFra, loginMulig, tjekWorkflow, type AutoRaekke } from '../../core/automationMotor';
import { kundeUdkast, type KundeBeskedInput, type UdkastData } from '../../core/kundeBesked';
import { beskedAfsender, egenFraAdresse, type BrandInfo } from '../../core/afsender';
import { formaalFor, gyldigtAfmeldToken } from '../../core/samtykke';
import type { SbKontekst } from './sbKatalog';
import { ApiFejl } from '../../core/fejl';

const KOLONNER = 'id, navn, aktiv, haendelse, sortering, formaal, maalgruppe, betingelser, forskydning, handlinger';
const tilRaekke = (k: string, w: Workflow) => ({ klient_id: k, id: w.id, navn: w.navn, aktiv: w.aktiv, haendelse: w.haendelse, sortering: w.sortering, formaal: formaalFor(w),
  maalgruppe: w.maalgruppe ?? { tilstand: 'alle', ydelser: [], tilvalg: [], partnere: [], medlemmer: false, nye_kunder: false, faktura: false },
  betingelser: w.betingelser, forskydning: w.forskydning, handlinger: w.handlinger });

export class SbAutomation {
  constructor(private readonly c: SbKontekst) {}

  private async egne(k: string): Promise<Workflow[]> {
    return (await this.c.k(this.c.sb.from('workflows').select(KOLONNER).eq('klient_id', k).order('sortering'))) as Workflow[];
  }
  /** Gemmes den første regel, lægges standarden ind først (ellers ville de andre forsvinde). */
  private async medStandard(k: string): Promise<Workflow[]> {
    const l = await this.egne(k);
    if (l.length) return l;
    await this.c.k(this.c.sb.from('workflows').insert(STANDARD_WORKFLOWS.map((w) => tilRaekke(k, w))));
    return structuredClone(STANDARD_WORKFLOWS);
  }

  workflows: Api['workflows'] = async () => {
    const l = await this.egne(await this.c.kraevKlient());
    return l.length ? l : structuredClone(STANDARD_WORKFLOWS);
  };
  gemWorkflow: Api['gemWorkflow'] = async (w) => {
    const k = await this.c.kraevKlient();
    const l = await this.medStandard(k);
    const ny = tjekWorkflow(w, () => 'wf_' + crypto.randomUUID().replace(/-/g, '').slice(0, 8));
    const findes = l.find((x) => x.id === ny.id);
    ny.sortering = findes ? findes.sortering : Math.max(0, ...l.map((x) => x.sortering)) + 1;
    await this.c.k(this.c.sb.from('workflows').upsert(tilRaekke(k, ny), { onConflict: 'klient_id,id' }));
    this.c.skift();
  };
  sletWorkflow: Api['sletWorkflow'] = async (id) => {
    const k = await this.c.kraevKlient();
    await this.medStandard(k);
    await this.c.k(this.c.sb.from('workflows').delete().eq('klient_id', k).eq('id', id));
    this.c.skift();
  };
  flytWorkflow: Api['flytWorkflow'] = async (id, retning) => {
    const k = await this.c.kraevKlient();
    const l = [...(await this.medStandard(k))].sort((a, b) => a.sortering - b.sortering);
    const i = l.findIndex((w) => w.id === id), j = i + retning;
    if (i < 0 || j < 0 || j >= l.length) return;
    [l[i], l[j]] = [l[j], l[i]];
    await Promise.all(l.map((w, n) => this.c.k(this.c.sb.from('workflows').update({ sortering: n + 1 }).eq('klient_id', k).eq('id', w.id))));
    this.c.skift();
  };
  testWorkflow: Api['testWorkflow'] = async (w, booking_id) => {
    const k = await this.c.kraevKlient();
    const d = (await this.c.k(this.c.sb.rpc('automation_test_data', { k, p_booking: booking_id }))) as { raekke: AutoRaekke; klient: Parameters<typeof autoKlient>[0] };
    const kl = autoKlient({ ...d.klient, workflows: null });
    const ktx = kontekstFra(d.raekke, kl);
    return {
      opfyldt: opfyldt(w, ktx), tid: koertid(w, ktx, Date.now()),
      handlinger: w.handlinger.map((h) => {
        const b = byggBesked(w, h, ktx, { login: false, steamer_email: d.raekke.steamer_email, firma_email: kl.indst.firma.email, klient: kl.slug });
        return { type: h.type, aktiv: h.aktiv, til: b.til, emne: b.emne, tekst: b.tekst };
      }),
    };
  };
  jobs: Api['jobs'] = async (dato) => (await this.c.k(this.c.sb.rpc('automation_planlagte', { k: await this.c.kraevKlient(), p_dato: dato ?? null }))) as Awaited<ReturnType<Api['jobs']>>;
  koerJobNu: Api['koerJobNu'] = async (id) => { await this.c.k(this.c.sb.rpc('automation_koer_nu', { k: await this.c.kraevKlient(), p_id: id })); this.c.skift(); };
  aktivitet: Api['aktivitet'] = async () => (await this.c.k(this.c.sb.rpc('automation_aktivitet', { k: await this.c.kraevKlient() }))) as Awaited<ReturnType<Api['aktivitet']>>;
  // ---------- Samtykke til markedsføring og afmelding (20261029300000_smaat.sql) ----------
  kundeSamtykke: Api['kundeSamtykke'] = async (kunde_id) => {
    const r = (await this.c.k(this.c.sb.rpc('kunde_samtykke', { k: await this.c.kraevKlient(), p_kunde: kunde_id }))) as Awaited<ReturnType<Api['kundeSamtykke']>> | null;
    if (!r) throw new ApiFejl('Kunden findes ikke.');
    return r;
  };
  saetSamtykke: Api['saetSamtykke'] = async (kunde_id, ja) => {
    await this.c.k(this.c.sb.rpc('saet_samtykke', { k: await this.c.kraevKlient(), p_kunde: kunde_id, p_ja: ja }));
    this.c.skift();
  };
  /** Offentlig: tokenet er adgangen (ingen login, ingen klient). */
  afmeld: Api['afmeld'] = async (token, afmeld = true) => {
    if (!gyldigtAfmeldToken(token)) return null;
    return (await this.c.k(this.c.sb.rpc('afmeld_markedsfoering', { p_token: token, p_afmeld: afmeld }))) as Awaited<ReturnType<Api['afmeld']>>;
  };
  // ---------- Kontoret sender selv til kunden (core/kundeBesked.ts, 20261029300000_smaat.sql) ----------
  /** Data til beskeden (bookingen, kunden, automatiseringerne og afsenderen) → udkastene, flettet som i testversionen. */
  private async udkast(a: KundeBeskedInput) {
    const k = await this.c.kraevKlient();
    type Data = { raekke: AutoRaekke | null; klient: Parameters<typeof autoKlient>[0]; kunde: { navn: string; email: string; telefon: string }; brand: BrandInfo | null; ny_tid: string | null; mail_domaene: string | null };
    const d = (await this.c.k(this.c.sb.rpc('kunde_besked_data', { k, p_booking: a.booking_id ?? null, p_kunde: a.kunde_id ?? null }))) as Data;
    const kl = autoKlient(d.klient);
    const ktx = d.raekke ? kontekstFra(d.raekke, kl) : null;
    const af = beskedAfsender({ navn: kl.navn, sms: kl.indst.beskeder.smsAfsender, design: null }, d.brand);
    const data: UdkastData = {
      kunde: d.kunde, k: ktx, workflows: kl.workflows, login: !!d.raekke && loginMulig(d.raekke, kl), nyTid: d.ny_tid,
      firma: ktx?.firma ?? (kl.indst.firma.navn || kl.navn), telefon: ktx?.firma_telefon ?? kl.indst.firma.telefon,
      afsender: { mail: `${af.navn} <${egenFraAdresse(kl.indst.beskeder.mailFra, d.mail_domaene || kl.domaene) ?? 'Freemis adresse'}>`, sms: af.sms, powered: af.powered }, klient: kl.slug, nu: Date.now(),
    };
    return { k, spaerret: kl.spaerret, udkast: kundeUdkast(a, data) };
  }
  beskedUdkast: Api['beskedUdkast'] = async (a) => { const u = await this.udkast(a); return { spaerret: u.spaerret, udkast: u.udkast }; };
  sendKundeBesked: Api['sendKundeBesked'] = async (a) => {
    const u = await this.udkast(a);
    const antal = (await this.c.k(this.c.sb.rpc('send_kunde_besked', { k: u.k, p_booking: a.booking_id ?? null, p_kunde: a.kunde_id ?? null,
      p_beskeder: u.udkast.map(({ kanal, emne, tekst, skabelon, link, ics }) => ({ kanal, emne, tekst, skabelon, link, ics: ics ?? null })) }))) as number;
    this.c.skift();
    return { antal };
  };
  gemBeskedKladde: Api['gemBeskedKladde'] = async (booking_id, k) => {
    await this.c.k(this.c.sb.rpc('gem_besked_kladde', { k: await this.c.kraevKlient(), p_booking: booking_id, p: k }));
    this.c.skift();
  };
  gensendBesked: Api['gensendBesked'] = async (id) => {
    await this.c.k(this.c.sb.rpc('gensend_besked', { k: await this.c.kraevKlient(), p_id: id }));
    this.c.skift();
  };
  beskedSpaerret: Api['beskedSpaerret'] = async () => !!(await this.c.k(this.c.sb.rpc('udsendelse_er_spaerret', { k: await this.c.kraevKlient() })));
  /** De nyeste 400 beskeder med kundens navn (beskeder_liste, 20261031090000_serie_gomore_beskeder.sql). */
  beskeder: Api['beskeder'] = async () => ((await this.c.k(this.c.sb.rpc('beskeder_liste', { k: await this.c.kraevKlient() }))) ?? []) as Besked[];
}
